<template>
  <div>
    <v-dialog v-model="showDialog" max-width="500" :persistent="loading">
      <v-card class="py-5">
        <v-card-title class="mb-3 font-weight-bold text-nowrap">
          {{ loading ? "Procesando productos del proveedor" : "Importar detalle desde Excel" }}
        </v-card-title>
        <v-card-text>
          <template v-if="loading">
            <div class="d-flex flex-column align-center justify-center py-10">
              <v-progress-circular
                indeterminate
                size="62"
                width="6"
                color="primary"
              />
              <div class="mt-4 text-center">
                Buscando coincidencias y resolviendo productos en la API.
              </div>
            </div>
          </template>
          <template v-else>
            <v-row>
              <v-col cols="12">
                <v-select
                  outlined
                  dense
                  :items="invoiceTypeOptions"
                  :value="localInvoiceType"
                  label="Tipo de Factura"
                  @change="handleInvoiceTypeChange"
                ></v-select>
              </v-col>
              <v-col cols="12" class="d-flex flex-row flex-wrap">
                <v-file-input
                  v-model="selectedFile"
                  style="min-width: 200px"
                  outlined
                  dense
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  label="Seleccione un Excel"
                  class="mr-3"
                  :rules="[maxFileSize]"
                  prepend-icon="mdi-file-excel"
                >
                </v-file-input>
                <v-btn
                  class="btn-save"
                  :disabled="loading || disabled"
                  @click="processImport"
                >
                  <v-icon class="mr-2">mdi-import</v-icon> Importar
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { userData } from "@/api/Currentuserdata";
import { PURCHASE_DETAIL_IMPORT_HEADERS as EXPECTED_HEADERS } from "@/helpers/purchaseDetailImportTemplate";

export default {
  name: "PurchaseDetailImport",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    providerId: {
      type: [String, Number],
      default: null,
    },
    noProvider: {
      type: Boolean,
      default: false,
    },
    invoiceType: {
      type: [String, Number],
      default: null,
    },
    invoiceTypeOptions: {
      type: Array,
      default: () => [],
    },
    barCodes: {
      type: Array,
      default: () => [],
    },
    inventoryItems: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      showDialog: false,
      selectedFile: null,
      localInvoiceType: null,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.showDialog = newVal;
      },
    },
    showDialog(newVal) {
      this.$emit("input", newVal);
      if (!newVal) {
        this.selectedFile = null;
      }
    },
    invoiceType: {
      immediate: true,
      handler(newVal) {
        this.localInvoiceType = newVal;
      },
    },
  },
  methods: {
    maxFileSize(file) {
      if (!file) return true;
      const allowedSize = 2 * 1024 * 1024;
      return file.size > allowedSize
        ? "El tamaño del archivo debe ser menor a 2 Mb"
        : true;
    },
    handleInvoiceTypeChange(value) {
      this.localInvoiceType = value;
      this.$emit("invoice-type-change", value);
    },
    async processImport() {
      const file = this.selectedFile;
      if (!file) {
        this.emitAlert(
          "warning",
          "Advertencia",
          "Por favor, selecciona un archivo Excel."
        );
        return;
      }

      if (!this.localInvoiceType) {
        this.emitAlert(
          "warning",
          "Advertencia",
          "Debe seleccionar el tipo de factura."
        );
        return;
      }

      const sizeValidation = this.maxFileSize(file);
      if (sizeValidation !== true) {
        this.emitAlert("warning", "Advertencia", sizeValidation);
        return;
      }

      this.loading = true;

      try {
        const rows = await this.readExcelFile(file);
        const result = await this.processRows(rows);

        if (!result.productsToLoad.length) {
          const firstError = result.skippedRows[0]?.error?.message;
          throw new Error(
            firstError ||
              "No se encontraron filas válidas para importar en el archivo seleccionado."
          );
        }

        this.$emit("import-detail", result);
        this.showDialog = false;
      } catch (error) {
        this.emitAlert(
          "warning",
          "Advertencia",
          error.message || "No se pudo procesar el archivo Excel."
        );
      } finally {
        this.loading = false;
        this.selectedFile = null;
      }
    },
    readExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, {
              type: "array",
              cellDates: true,
            });
            const firstSheetName = workbook.SheetNames[0];

            if (!firstSheetName) {
              throw new Error("El archivo Excel no contiene hojas disponibles.");
            }

            const worksheet = workbook.Sheets[firstSheetName];
            const matrix = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: false,
              defval: "",
              dateNF: "yyyy-mm-dd",
            });

            const filteredMatrix = matrix.filter((row) =>
              Array.isArray(row)
                ? row.some((value) => String(value || "").trim() !== "")
                : false
            );

            if (!filteredMatrix.length) {
              throw new Error("El archivo Excel no contiene datos para importar.");
            }

            this.validateHeaders(filteredMatrix[0]);

            const headers = filteredMatrix[0].map((header) => String(header || "").trim());
            const rows = filteredMatrix.slice(1).map((row, index) => {
              const rowObject = {
                __rowNumber: index + 2,
              };

              headers.forEach((header, headerIndex) => {
                rowObject[header] = row[headerIndex] === undefined ? "" : row[headerIndex];
              });

              return rowObject;
            });

            resolve(rows);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => {
          reject(new Error("No se pudo leer el archivo seleccionado."));
        };

        reader.readAsArrayBuffer(file);
      });
    },
    validateHeaders(headers) {
      const normalizedHeaders = headers.map((header) =>
        this.normalizeHeader(header)
      );
      const missingHeaders = EXPECTED_HEADERS.filter(
        (header) => !normalizedHeaders.includes(this.normalizeHeader(header))
      );

      if (missingHeaders.length) {
        throw new Error(
          `La plantilla no es válida. Faltan las columnas: ${missingHeaders.join(
            ", "
          )}.`
        );
      }
    },
    async processRows(rows) {
      const productsToLoad = [];
      const skippedRows = [];
      const pendingRows = [];
      const remoteCodes = new Set();

      for (const row of rows) {
        const rowNumber = row.__rowNumber;

        if (this.isEmptyRow(row)) {
          continue;
        }

        try {
          const quantity = this.parseNumber(row["Cantidad"]);
          const price = this.parseNumber(row["P. Unit."]);
          const taxFlags = this.resolveTaxFlags(row);

          if (quantity <= 0) {
            throw this.buildRowError(
              "INVALID_ROW_DATA",
              rowNumber,
              "La cantidad debe ser mayor a 0."
            );
          }

          if (price < 0 || Number.isNaN(price)) {
            throw this.buildRowError(
              "INVALID_ROW_DATA",
              rowNumber,
              "El precio unitario no es válido."
            );
          }

          const localProductMatch = this.findLocalProductMatch(row);

          if (localProductMatch) {
            productsToLoad.push(
              this.buildMatchedProduct({
                row,
                productMatch: localProductMatch,
                quantity,
                price,
                taxFlags,
              })
            );
            continue;
          }

          const productId = this.getTrimmedValue(row["ID de Producto"]);
          const alternateCode = this.getTrimmedValue(row["Código alterno"]);

          if (!productId && !alternateCode) {
            throw this.buildRowError(
              "UNKNOW_ID_ITEM",
              rowNumber,
              "El producto no se encuentra asociado a ningún producto del proveedor."
            );
          }

          if (productId) {
            remoteCodes.add(productId);
          }

          if (alternateCode) {
            remoteCodes.add(alternateCode);
          }

          const pendingIndex = productsToLoad.length;
          productsToLoad.push(null);
          pendingRows.push({
            index: pendingIndex,
            row,
            quantity,
            price,
            taxFlags,
            productId,
            alternateCode,
          });
        } catch (error) {
          if (this.isSearchRecoverableError(error.code)) {
            productsToLoad.push(this.buildPendingProduct(row, error));
            continue;
          }

          skippedRows.push(this.buildSkippedRow(row, error));
        }
      }

      const remoteProducts = remoteCodes.size
        ? await this.fetchProductsByCodes(Array.from(remoteCodes))
        : [];
      const remoteLookup = this.buildProductLookupMap(remoteProducts);

      for (const pendingRow of pendingRows) {
        const productMatch = this.findRemoteProductMatch(pendingRow, remoteLookup);

        if (productMatch) {
          productsToLoad[pendingRow.index] =
            this.buildMatchedProduct({
              row: pendingRow.row,
              productMatch,
              quantity: pendingRow.quantity,
              price: pendingRow.price,
              taxFlags: pendingRow.taxFlags,
            });
          continue;
        }

        const error = this.buildRowError(
          "UNKNOW_ID_ITEM",
          pendingRow.row.__rowNumber,
          "El producto no se encuentra asociado a ningún producto del proveedor."
        );

        productsToLoad[pendingRow.index] = this.buildPendingProduct(pendingRow.row, error);
      }

      return { productsToLoad, skippedRows };
    },
    findLocalProductMatch(row) {
      const productId = this.getTrimmedValue(row["ID de Producto"]);
      const alternateCode = this.getTrimmedValue(row["Código alterno"]);
      const hasLocalInventory = this.hasLocalInventoryData();

      if (hasLocalInventory) {
        if (productId) {
          const itemById = this.inventoryItems.find(
            (item) => this.getTrimmedValue(item.ID_Item) === productId
          );

          if (itemById) {
            return {
              item: itemById,
              barCode: this.findFirstBarcodeById(itemById.ID_Item),
            };
          }
        }

        if (alternateCode) {
          const barcodeMatches = this.barCodes.filter(
            (item) => this.getTrimmedValue(item.BarCode) === alternateCode
          );

          if (barcodeMatches.length > 1) {
            throw this.buildRowError(
              "DUPLICATE_ALTERNATE_CODE",
              row.__rowNumber,
              `El código alterno "${alternateCode}" tiene múltiples coincidencias.`
            );
          }

          if (barcodeMatches.length === 1) {
            const matchedBarcode = barcodeMatches[0];
            const inventoryItem = this.inventoryItems.find(
              (item) =>
                this.getTrimmedValue(item.ID_Item) ===
                this.getTrimmedValue(matchedBarcode.ID_Item)
            );

            if (inventoryItem) {
              return {
                item: inventoryItem,
                barCode: matchedBarcode.BarCode,
              };
            }
          }
        }
      }

      return null;
    },
    findRemoteProductMatch(rowData, remoteLookup) {
      const productId = this.getTrimmedValue(rowData.productId);
      const alternateCode = this.getTrimmedValue(rowData.alternateCode);
      const normalizedProductId = this.normalizeProductCode(productId);
      const normalizedAlternateCode = this.normalizeProductCode(alternateCode);

      if ((productId && remoteLookup.has(productId)) || (normalizedProductId && remoteLookup.has(normalizedProductId))) {
        const matchedItem = remoteLookup.get(productId) || remoteLookup.get(normalizedProductId);
        return {
          item: matchedItem,
          barCode: this.findFirstBarcodeById(matchedItem.ID_Item),
        };
      }

      if ((alternateCode && remoteLookup.has(alternateCode)) || (normalizedAlternateCode && remoteLookup.has(normalizedAlternateCode))) {
        const matchedItem = remoteLookup.get(alternateCode) || remoteLookup.get(normalizedAlternateCode);
        return {
          item: matchedItem,
          barCode: alternateCode || normalizedAlternateCode,
        };
      }

      return null;
    },
    buildProductLookupMap(items = []) {
      const lookup = new Map();

      items.forEach((item) => {
        [
          item?.ID_Item,
          item?.BarCode,
          item?.PrincipalBarCode,
          item?.ItemBarCode,
          item?.CodigoAlterno,
        ].forEach((code) => {
          const normalizedCode = this.normalizeProductCode(code);
          if (normalizedCode && !lookup.has(normalizedCode)) {
            lookup.set(normalizedCode, item);
          }
        });
      });

      return lookup;
    },
    normalizeProductCode(value) {
      return this.getTrimmedValue(value).replace(/[\s-]+/g, "");
    },
    async fetchProductsByCodes(codes) {
      if (!Array.isArray(codes) || !codes.length) {
        return [];
      }

      try {
        const response = await this.$API.products.getProductsToBillForPurchaseOrdersByCodes({
          business: this.getCurrentBusinessId(),
          branch: this.getCurrentBranchId(),
          provider: this.providerId,
          noProvider: this.noProvider,
          codes,
        });

        return Array.isArray(response) ? response : [];
      } catch (error) {
        console.warn("Error resolviendo productos en lote:", error);
        return [];
      }
    },
    hasLocalInventoryData() {
      return Array.isArray(this.inventoryItems) && this.inventoryItems.length > 0;
    },
    getCurrentBusinessId() {
      return userData()?.businessid || "";
    },
    getCurrentBranchId() {
      return userData()?.branch || "";
    },
    async searchProductById(productId, apiCache) {
      if (!productId) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(apiCache.productById, productId)) {
        return apiCache.productById[productId];
      }

      try {
        const response = await this.$API.products.getProductsToBillForPurchaseOrdersFiltered({
          business: this.getCurrentBusinessId(),
          provider: this.providerId,
          noProvider: this.noProvider,
          filter: productId,
          showAllRecords: false,
        });
        const products = Array.isArray(response) ? response : [];
        const exactMatch = products.find(
          (item) => this.getTrimmedValue(item.ID_Item) === productId
        );

        apiCache.productById[productId] = exactMatch || null;
        return apiCache.productById[productId];
      } catch (error) {
        apiCache.productById[productId] = null;
        return null;
      }
    },
    async searchProductByBarcode(barcode, apiCache) {
      if (!barcode) {
        return null;
      }

      if (Object.prototype.hasOwnProperty.call(apiCache.barcodeByValue, barcode)) {
        return apiCache.barcodeByValue[barcode];
      }

      try {
        if (this.noProvider || this.providerId) {
          const response = await this.$API.inventoryitemsbarcodes.getBarCodeProvidersById({
            ID_Provider: this.providerId,
            barcode,
            noProvider: this.noProvider,
          });

          if (response && response.ID_Item) {
            apiCache.barcodeByValue[barcode] = response;
            return apiCache.barcodeByValue[barcode];
          }
        }

        const response = await this.$API.products.getProductsToBillForPurchaseOrdersFiltered({
          business: this.getCurrentBusinessId(),
          provider: this.providerId,
          noProvider: this.noProvider,
          filter: barcode,
          showAllRecords: false,
        });

        const products = Array.isArray(response) ? response : [];
        const exactMatch = products.find((item) => {
          const itemBarCode =
            this.getTrimmedValue(item.BarCode) ||
            this.getTrimmedValue(item.ItemBarCode) ||
            this.getTrimmedValue(item.CodigoAlterno);

          return itemBarCode === barcode;
        });

        apiCache.barcodeByValue[barcode] = exactMatch || null;
        return apiCache.barcodeByValue[barcode];
      } catch (error) {
        apiCache.barcodeByValue[barcode] = null;
        return null;
      }
    },
    buildMatchedProduct({ row, productMatch, quantity, price, taxFlags }) {
      return {
        ...productMatch.item,
        ID_ItemBatch: this.getTrimmedValue(row["Lote"]),
        ExpirationDate: this.getTrimmedValue(row["Fecha de expiración"]),
        code:
          productMatch.barCode ||
          this.getTrimmedValue(row["Código alterno"]) ||
          "",
        BarCode:
          productMatch.barCode ||
          this.getTrimmedValue(row["Código alterno"]) ||
          "",
        description:
          this.getTrimmedValue(row["Nombre de Producto"]) ||
          productMatch.item.ItemName ||
          productMatch.item.ItemComercialName ||
          "",
        quantity,
        price,
        noGlobalDicountPrice: price,
        priceFixedDecimal: price.toFixed(2),
        id: productMatch.item.ID_Item,
        ID_Item: productMatch.item.ID_Item,
        ID_Business: productMatch.item.ID_Business,
        ID_Branch: productMatch.item.ID_Branch,
        warehouse:
          productMatch.item.Warehouse ||
          productMatch.item.inventory_warehouse?.Warehouse ||
          null,
        ID_Warehouse:
          productMatch.item.ID_Warehouse ||
          productMatch.item.inventory_warehouse?.ID_Warehouse ||
          null,
        IsExento: taxFlags.IsExento,
        IsNoSujeto: taxFlags.IsNoSujeto,
        error: {
          code: null,
          message: "",
        },
        importRowNumber: row.__rowNumber,
      };
    },
    buildPendingProduct(row, error) {
      const quantity = this.parseNumber(row["Cantidad"]);
      const price = this.parseNumber(row["P. Unit."]);
      const taxFlags = this.resolveTaxFlags(row);
      const fallbackCode =
        this.getTrimmedValue(row["Código alterno"]) ||
        `FILA-${row.__rowNumber}`;

      return {
        ID_ItemBatch: this.getTrimmedValue(row["Lote"]),
        ExpirationDate: this.getTrimmedValue(row["Fecha de expiración"]),
        code: fallbackCode,
        BarCode: this.getTrimmedValue(row["Código alterno"]) || "",
        description: this.getTrimmedValue(row["Nombre de Producto"]) || "",
        quantity: Number.isNaN(quantity) ? 0 : quantity,
        price: Number.isNaN(price) ? 0 : price,
        noGlobalDicountPrice: Number.isNaN(price) ? 0 : price,
        priceFixedDecimal: Number.isNaN(price) ? "0.00" : price.toFixed(2),
        IsExento: taxFlags.IsExento,
        IsNoSujeto: taxFlags.IsNoSujeto,
        error: {
          code: error.code || "UNKNOW_ID_ITEM",
          message: error.message || "No se pudo encontrar el producto.",
        },
        importRowNumber: row.__rowNumber,
      };
    },
    buildSkippedRow(row, error) {
      return {
        rowNumber: row.__rowNumber,
        row,
        error: {
          code: error.code || "INVALID_ROW",
          message: error.message || "No se pudo procesar la fila.",
        },
      };
    },
    isSearchRecoverableError(code) {
      return code === "UNKNOW_ID_ITEM" || code === "DUPLICATE_ALTERNATE_CODE";
    },
    resolveTaxFlags(row) {
      const hasExempt = this.hasCellValue(row["Exento"]);
      const hasNotSubject = this.hasCellValue(row["No Sujeto"]);

      if (hasExempt && hasNotSubject) {
        throw this.buildRowError(
          "MULTIPLE_TAX_TYPES",
          row.__rowNumber,
          "La fila tiene valores en Exento y No Sujeto al mismo tiempo."
        );
      }

      return {
        IsExento: hasExempt,
        IsNoSujeto: hasNotSubject,
      };
    },
    normalizeHeader(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\./g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    },
    parseNumber(value) {
      if (typeof value === "number") {
        return value;
      }

      const trimmedValue = this.getTrimmedValue(value);
      if (!trimmedValue) {
        return NaN;
      }

      if (trimmedValue.includes(",") && trimmedValue.includes(".")) {
        return parseFloat(trimmedValue.replace(/,/g, ""));
      }

      if (trimmedValue.includes(",")) {
        return parseFloat(trimmedValue.replace(",", "."));
      }

      return parseFloat(trimmedValue);
    },
    hasCellValue(value) {
      const trimmedValue = this.getTrimmedValue(value);
      return (
        trimmedValue !== "" &&
        trimmedValue !== "0" &&
        trimmedValue !== "0.00" &&
        trimmedValue.toLowerCase() !== "false"
      );
    },
    isEmptyRow(row) {
      return EXPECTED_HEADERS.every((header) => {
        return this.getTrimmedValue(row[header]) === "";
      });
    },
    findFirstBarcodeById(idItem) {
      const match = this.barCodes.find(
        (item) => this.getTrimmedValue(item.ID_Item) === this.getTrimmedValue(idItem)
      );

      return match ? match.BarCode : "";
    },
    getTrimmedValue(value) {
      return String(value === null || value === undefined ? "" : value).trim();
    },
    buildRowError(code, rowNumber, message) {
      const error = new Error(`Fila ${rowNumber}: ${message}`);
      error.code = code;
      return error;
    },
    emitAlert(type, title, message) {
      this.$emit("show-alert", {
        type,
        title,
        message,
      });
    },
  },
};
</script>
